import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import { GoOrganization } from "react-icons/go";
import { tokens as T, hairline, surface } from "../../../styles/designTokens";
import { tokens as activity } from "../../activity-logs/styles";
import { Capsule } from "../../creditos/components/ControlBar";
import { SANS } from "../../creditos/styles";
import { formatCNPJ } from "../../creditos/dashboardData";

/**
 * Filtro de empresas (matriz e filiais), com seleção múltipla. A lista vem de
 * /projects/:id/empresas-industriais, a mesma do cadastro do projeto.
 */

const Anchor = styled.div`
  position: relative;
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  width: 340px;
  max-width: calc(100vw - 32px);
  padding: 8px;
  border-radius: 18px;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.text};
  /* Opaco: dentro da TopBar, que já tem backdrop-filter, o blur do popover não funciona */
  background: ${({ theme }) => (activity.dk(theme) ? "#232327" : "#ffffff")};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.12);

  .top { display: flex; align-items: center; justify-content: space-between; padding: 6px 8px 8px; font-size: 0.8em; color: ${({ theme }) => activity.label2(theme)}; }
  .top button { border: none; background: none; padding: 0; color: ${T.brand}; font: inherit; cursor: pointer; }
  .list { max-height: 320px; overflow-y: auto; }
  .opt { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px; border: none; border-radius: 10px; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
  .opt:hover { background: ${({ theme }) => surface(theme, 1)}; }
  .opt:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .opt b { display: block; font-size: 0.84em; font-weight: 500; }
  .opt small { display: block; font-size: 0.74em; color: ${({ theme }) => activity.label2(theme)}; }
  .check { flex-shrink: 0; margin-left: auto; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #fff; }
  .check.on { background: ${T.brand}; }
  .check.off { box-shadow: inset 0 0 0 1.5px ${({ theme }) => hairline(theme, true)}; }
  .empty { padding: 14px 8px; font-size: 0.82em; color: ${({ theme }) => activity.label2(theme)}; }
`;

export default function EmpresasFilter({ empresas, value, onChange }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open]);

  const toggle = (cnpj) => onChange(value.includes(cnpj) ? value.filter((c) => c !== cnpj) : [...value, cnpj]);
  const only = value.length === 1 ? empresas.find((e) => e.cnpj === value[0]) : null;
  let label = "Todas as empresas";
  if (only) label = only.tipo === "matriz" ? "Matriz" : `Filial ${formatCNPJ(only.cnpj).slice(-7)}`;
  else if (value.length > 1) label = `${value.length} empresas`;

  return (
    <Anchor ref={anchorRef}>
      <Capsule type="button" $active={value.length > 0} onClick={() => setOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={open}>
        <GoOrganization />
        {label}
        <FiChevronDown className="chev" />
      </Capsule>
      {open && (
        <Popover role="dialog" aria-label="Filtrar por empresa">
          <div className="top">
            <span>{empresas.length} {empresas.length === 1 ? "empresa" : "empresas"} no projeto</span>
            {value.length > 0 && <button type="button" onClick={() => onChange([])}>Limpar</button>}
          </div>
          <div className="list">
            {empresas.map((e) => {
              const on = value.includes(e.cnpj);
              return (
                <button key={e.cnpj} type="button" className="opt" aria-pressed={on} onClick={() => toggle(e.cnpj)}>
                  <span>
                    <b>{e.tipo === "matriz" ? "Matriz" : "Filial"}</b>
                    <small>{formatCNPJ(e.cnpj)}</small>
                  </span>
                  <span className={`check ${on ? "on" : "off"}`} aria-hidden="true">{on && <FiCheck />}</span>
                </button>
              );
            })}
            {empresas.length === 0 && <div className="empty">Nenhuma empresa encontrada neste projeto.</div>}
          </div>
        </Popover>
      )}
    </Anchor>
  );
}
